import Logger from '@/logger'
import StorageHelper from './helper'

import Store from '@/store'
import { MUTATIONS } from '@/store/mutations'

import ExternalContext from '../auth/external-context'
import GuestContext from '@/auth/guest-context'
import UserContext from '@/auth/user-context'


/**
 * Name to use for logging.
 * @type {string}
 */
const LOG_NAME = 'Local Storage'

/**
 * Return the the guestLocal store or sessionStore depending on
 * cookie consent data.
 * @async
 * @returns {Promise<LocalStorageGateway>}
 */
async function getLocalStore() {
    return await StorageHelper.storageDisabledAsync()
        ? StorageHelper.sessionStore
        : StorageHelper.guestLocal
}

/**
 * Storage access methods for context.
 */
const Context = {

    /**
     * Registers the auth context for a user.
     *
     * @param {string} userName Authorised username.
     * @param {string} token Bearer token.
     * @returns {Promise<Object>} The persisted data, adjusted to store.
     */
    async persistAsync(userName, token) {
        const localStorage = await getLocalStore()

        try {
            const currentCxt = Store.state.authContext
            if (currentCxt && userName === currentCxt.id) return currentCxt

            Logger.log('Adding User Auth context', LOG_NAME)
            const cxt = new UserContext(userName, token)
            const updatedCxt = await localStorage.context.upsertAsync(cxt)
            Store.commit(MUTATIONS.setAuthContext, cxt)

            return updatedCxt
        } catch (err) {
            Logger.log('Unable to persist user auth context', LOG_NAME)
            throw err
        }
    },

    /**
     * Registers the auth context for an external user.
     *
     * @param {string} key Key of authorised user.
     * @param {string} token Bearer token.
     * @returns {Promise<Object>} The persisted data, adjusted to store.
     */
    async persistExternalUserAsync(key, token) {

        try {
            const currentCxt = Store.state.authContext

            if (currentCxt && key === currentCxt.id)
                return currentCxt

            Logger.log('Clearing local storage.', LOG_NAME)
            const localStorage = await getLocalStore()

            await localStorage.clearContextAsync()

            Logger.log('Adding External auth context', LOG_NAME)
            const cxt = new ExternalContext(key, token)

            Store.commit(MUTATIONS.setAuthContext, cxt)

            return cxt
        } catch (err) {
            Logger.log('Unable to persist external user auth context', LOG_NAME)
            throw err
        }
    },

    /**
     * Registers the auth context for a guest.
     *
     * @param {string} dynamicKey Guest key.
     * @returns {Promise<Object>} The persisted data, adjusted to store.
     */
    async persistGuestAsync(dynamicKey) {
        const localStorage = await getLocalStore()

        try {
            const currentCxt = Store.state.authContext
            if (currentCxt && dynamicKey === currentCxt.id) return currentCxt

            Logger.log('Adding Guest Auth context', LOG_NAME)
            const cxt = new GuestContext(dynamicKey)
            const updatedCxt = await localStorage.context.upsertAsync(cxt)
            Store.commit(MUTATIONS.setAuthContext, cxt)
            return updatedCxt
        } catch (err) {
            Logger.log('Unable to persist guest auth context', LOG_NAME)
            throw err
        }
    },

    /**
     * Gets the last stored authentication context.
     * @returns {Promise<Context>} The retrieved context.
     */
    async retrieveAsync() {

        /**
         * Builds a temporary guest context. This will be replaced when an address is selected and ReCaptcha is satisfied.
         *
         * @returns {GuestContext}
         */
        const buildTempGuest = function() {
            const cxt = new GuestContext()
            Store.commit(MUTATIONS.setAuthContext, cxt)
            return cxt
        }

        if (await StorageHelper.storageDisabledAsync()) return buildTempGuest()

        const localStorage = await getLocalStore()

        try {
            Logger.log('Retrieving Auth context', LOG_NAME)
            const value = await localStorage.context.getAsync()
            if (!value || value.token.isExpired) return buildTempGuest()

            Store.commit(MUTATIONS.setAuthContext, value)
            return value
        } catch (err){
            Logger.log('Unable to retrieve auth context', LOG_NAME)
            throw err
        }
    },
}

export default Context
